<!--

	A dialog to let the User confirm/modify the overlay_data for all
	editable main TVSpots (from the bundles) of the Campaign he is trying
	to activate.

	Emits a 'configured' signal with data that can be used to send
	alongside the campaign activation request so that the chosen values
	are used as overlay_data for the entries that will be created.

-->

<template>
  <div>
    <v-dialog
      :value="show"
      :width="$store.state.common.largeDialogWidth"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      scrollable
      lazy
      @input="(val) => $emit('update:show', val)"
    >
      <v-card>
        <v-card-title :class="{'pb-0': showBrandingsDropdown}">
          <v-layout column>
            <v-layout align-center>
              <div class="headline">
                {{ $t('campaignEnableHint') }}
              </div>
              <v-btn
                class="ml-auto mr-0 my-0"
                :disabled="loading"
                flat
                icon
                :title="$t('cancel')"
                @click="abort()"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </v-layout>
            <div class="subheading">
              <span class="msr--text">{{
                $t('campaignContainsEditableContent')
              }}</span>
              {{ $t('campaignPleaseConfigureOverlayData') }}
            </div>
          </v-layout>
          <v-layout v-if="showBrandingsDropdown">
            <!-- Branding Dropdown -->
            <tv-template-branding-widget
              :key="lastBrandingAppliedId"
              ref="brandingwidget"
              class="ml-auto"
              :title="lastBrandingApplied ? lastBrandingApplied.name : null"
              :overlay-options="firstBrandableTvspot.overlay_options"
              :brandings="brandings"
              :active="lastBrandingApplied !== null"
              @applied="onBrandingApplied"
            />
          </v-layout>
        </v-card-title>
        <v-divider />
        <v-card-text style="background-color: rgba(0, 0, 0, 0.04)">
          <v-card
            v-for="tvspot in tvspots"
            :key="tvspot.id"
            class="mb-3"
          >
            <v-card-text class="pt-2 pb-0">
              <!-- Display as Spot for editable TVSpots with layoutelements. -->
              <spot
                v-if="!tvspot.editable_template"
                :spot="tvspot"
                :ctype="tvspot.ctype"
                :show-category="false"
                :show-new-badge="false"
                :show-divider="false"
                :show-indicators="false"
                :on-click="doNothing"
              >
                {{ tvspot.title }}
                <div slot="actions" />
              </spot>
              <!-- Display as live HTML-preview for editable TVSpots with HTML. -->
              <div v-else>
                <strong
                  class="d-block mt-1 mb-2"
                  style="font-size: 1.15em"
                >
                  {{ tvspot.title }}
                </strong>
                <v-layout justify-center>
                  <tv-template-preview
                    ref="preview"
                    class="mb-2"
                    :data-maintvspotid="tvspot.id"
                    :html-overlay="tvspot.editable_template.html_overlay"
                    :background-image="
                      !tvspot.editable_template
                        .use_tvspot_video_for_background &&
                        tvspot.editable_template
                          .use_tvspot_poster_for_preview_background
                        ? tvspot.editable_template.video_poster_for_editing
                        : null
                    "
                    :background-video="
                      tvspot.editable_template.use_tvspot_video_for_background
                        ? tvspot.preview_background_url
                        : null
                    "
                    :width="tvspot.tvformat.width"
                    :height="tvspot.tvformat.height"
                    :max-width="$store.state.common.largeDialogWidth - 80"
                    :max-height="420"
                    :is-multiscreen="tvspot.tvformat.is_multiscreen_format"
                    :duration="tvspot.duration"
                    :fake-player-progress="tvspot.editable_template.is_animated"
                    :fake-fade-in-fade-out="
                      tvspot.editable_template.fade_enabled
                    "
                    :fade-in-start="tvspot.editable_template.fadein_start"
                    :fade-in-stop="tvspot.editable_template.fadein_stop"
                    :fade-out-start="tvspot.editable_template.fadeout_start"
                    :fade-out-stop="tvspot.editable_template.fadeout_stop"
                    :onload-callback="
                      () =>
                        $store
                          .dispatch('MAYBE_LIST_BRANDINGS')
                          .then(() =>
                            populateAllIframesWithInitialOverlayData(
                              initialBranding
                            )
                          )
                    "
                  />
                </v-layout>
              </div>
            </v-card-text>
            <v-card-actions class="pt-1">
              <overlay-data
                ref="overlaydata"
                :data-spotid="tvspot.id"
                class="grow-1"
                :overlay-options="tvspot.overlay_options"
                validate-on-start
                hide-flagged-groups-during-activation
                @change-image="
                  (opts) =>
                    onEntryCreationDialogImageChangeRequested(tvspot, opts)
                "
                @changed="
                  () =>
                    onEntryCreationDialogOverlayDataChanged(lastBrandingApplied)
                "
              />
            </v-card-actions>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            round
            :loading="loading"
            :disabled="!isAllOverlayDataValid"
            :label="$t('activateNow')"
            data-cy="confirm-campaign-activation-btn"
            @click="confirm()"
          />
          <v-spacer />
        </v-card-actions>
        <v-spacer />
      </v-card>
    </v-dialog>
    <!-- Image Replacement for EditableTVSpotTemplates -->
    <image-replacement
      ref="imagereplacement"
      @cropped="onEntryCreationDialogImageChangeConfirmed"
    />
  </div>
</template>

<script>
import {deepCopyObject, getMainTVSpotFromBundle} from '@/utils';

// FIXME: Modularize this as the mixin isn't meant for multiple dialogs.
// We are reusing some methods from this because they already deal with multiple spots:
// - onEntryCreationDialogImageChangeConfirmed()
// - onEntryCreationDialogImageChangeRequested()
// - onEntryCreationDialogOverlayDataChanged()
// - populateAllIframesWithInitialOverlayData()
// - refreshAllIframesWithOverlayData()
// - validateOverlayDataWidgets()
import {
  chooseDefaultBrandingFromBrandings,
  getFallbackBrandingFromTVSpotTemplateData,
} from '@/mixins/tvtemplateBrandings';
import entryCreationDialogOverlayData from '@/mixins/entryCreationDialogOverlayData.js';

import BtnOk from '@/components/ButtonOk';
import ImageReplacement from '@/components/ImageReplacement';
import OverlayData from '@/components/OverlayData';
import Spot from '@/components/Spot';
import TvTemplateBrandingWidget from '@/components/TvTemplateBrandingWidget.vue';
import TvTemplatePreview from '@/components/TvTemplatePreview';

export default {
  components: {
    BtnOk,
    ImageReplacement,
    OverlayData,
    Spot,
    TvTemplateBrandingWidget,
    TvTemplatePreview,
  },
  mixins: [entryCreationDialogOverlayData],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    selectedEditableTvSpotBundles: {
      type: Array[Object],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      isAllOverlayDataValid: false,
    };
  },
  computed: {
    tvspots() {
      return this.selectedEditableTvSpotBundles.map((bundle) => {
        return getMainTVSpotFromBundle(bundle);
      });
    },
    idToTvspot() {
      return Object.fromEntries(
        this.tvspots.map((tvspot) => [tvspot.id, tvspot])
      );
    },
    brandableTvspots() {
      return this.tvspots.filter(
        (tvspot) =>
          !!tvspot.editable_template && tvspot.editable_template.is_brandable
      );
    },
    firstBrandableTvspot() {
      if (this.brandableTvspots.length === 0) {
        return null;
      }
      return this.brandableTvspots[0];
    },
    // FIXME: For compatibility with entryCreationDialogOverlayData:onEntryCreationDialogImageChangeConfirmed().
    contents() {
      return this.selectedEditableTvSpotBundles;
    },
    brandings() {
      return this.$store.state.common.brandings;
    },
    lastBrandingApplied: {
      get() {
        return this.$store.state.common.lastBrandingApplied;
      },
      set(branding) {
        this.$store.commit('SET_LAST_BRANDING_APPLIED', branding);
      },
    },
    lastBrandingAppliedId() {
      if (!this.lastBrandingApplied) {
        return 0;
      }
      return this.lastBrandingApplied.id;
    },
    defaultBranding() {
      return chooseDefaultBrandingFromBrandings(this.brandings);
    },
    fallbackBranding() {
      return getFallbackBrandingFromTVSpotTemplateData(
        this.firstBrandableTvspot
      );
    },
    initialBranding() {
      return (
        this.lastBrandingApplied ||
        this.defaultBranding ||
        this.fallbackBranding
      );
    },
    showBrandingsDropdown() {
      return (
        this.$store.getters.userHasContentCreatorBooking &&
        this.brandableTvspots.length > 0
      );
    },
  },
  watch: {
    show: function (show) {
      if (show) {
        // Initialize Branding dropdown, if needed.
        this.$store
          .dispatch('MAYBE_LIST_BRANDINGS')
          .then(() => this.initializeBranding());

        this.$nextTick(() => {
          this.validateOverlayDataWidgets();
        });
      }
    },
  },
  methods: {
    initializeBranding() {
      if (this.brandableTvspots.length === 0 || !this.initialBranding) {
        return;
      }

      // Update UI widgets and (implicitly) the iframe preview.
      const brandingWidget = this.$refs.brandingwidget;
      if (brandingWidget) {
        brandingWidget.applyBranding(this.initialBranding);
      }
      // Note: We may not have a Branding-widget, but still need to
      //   apply the fallback Branding.
      this.onBrandingApplied({branding: this.initialBranding});
    },
    onBrandingApplied(context) {
      this.lastBrandingApplied = deepCopyObject(context.branding);
      this.refreshAllIframesWithOverlayData(this.lastBrandingApplied);
    },
    injectLastBrandingAppliedIntoOverlayData(overlayData) {
      if (!overlayData.branding) {
        overlayData.branding = {};
      }
      if (this.lastBrandingApplied) {
        overlayData.branding = this.lastBrandingApplied;
      }
    },
    /** No-op to prevent jumping to a Spot's page on click. */
    doNothing() {
      // Do Nothing.
    },
    getSpotsOverlayData() {
      const spotIdToOverlayData = {};

      this.$refs.overlaydata.forEach((ref) => {
        const overlayData = ref.getOverlayData();
        const tvspotId = ref.$el.getAttribute('data-spotid');

        // Inject TVSpot duration and Branding.
        const tvspot = this.idToTvspot[tvspotId];
        if (tvspot) {
          overlayData.duration = tvspot.duration;
        }
        this.injectLastBrandingAppliedIntoOverlayData(overlayData);

        spotIdToOverlayData[tvspotId] = overlayData;
      });

      return spotIdToOverlayData;
    },
    reset() {
      this.loading = false;
    },
    closeDialog() {
      this.$emit('update:show', false);
    },
    abort() {
      this.reset();
      this.closeDialog();
    },
    /**
     * Emit a mapping of TVSpot ids to overlay_data.
     *
     * Example:
     *
     * {
     *    123: {
     *    	"price1": 12.34,
     *    	"price2": 45.67,
     *    	"_labels": {
     *       "price1": "Preis 1",
     *       "price2": "Preis 2",
     *    	}
     *    }
     *  }
     *
     */
    confirm() {
      const spotIdToOverlayData = this.getSpotsOverlayData();
      this.$emit('configured', spotIdToOverlayData);
      this.abort();
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/CampaignOverlayDataDialog.vue